/* =================================================================================================
 * Fiche « look complet » — fenêtre de personnalisation du look.
 *
 * Deux façons d'acheter, et c'est toute la logique de ce fichier :
 *
 * 1. Le look entier part au panier comme UN produit, à son tarif pack. C'est
 *    ce produit-là qui porte la remise ; elle n'existe nulle part ailleurs.
 *
 * 2. Dès qu'un article est retiré, ce produit ne correspond plus à ce que le
 *    client achète. On ajoute donc les articles restants un par un, à leur
 *    prix unitaire — d'où l'avertissement affiché dans la fenêtre.
 *
 * Look incomplet (`data-look-complete="false"`) : un article est épuisé, ou
 * le produit look lui-même. Le tarif pack ne s'applique plus. La fenêtre
 * s'ouvre directement sur les articles disponibles, prêts à être ajoutés à
 * leur prix unitaire sans rien retirer ; l'article épuisé reste visible,
 * grisé, et le message de la fenêtre explique pourquoi.
 * ===============================================================================================*/

(function () {
  'use strict';

  if (customElements.get('look-editor')) return;

  class LookEditor extends HTMLElement {
    connectedCallback() {
      // La fenêtre est déplacée hors de l'élément (voir plus bas) : un second
      // passage ici ne la retrouverait plus.
      if (this.dataset.pret) return;

      this.trigger = this.querySelector('.look-editor__trigger');
      this.overlay = this.querySelector('[data-overlay]');
      this.dialog = this.querySelector('.look-editor__dialog');
      this.items = Array.from(this.querySelectorAll('[data-item]'));
      if (!this.trigger || !this.overlay || this.items.length === 0) return;

      this.notice = this.querySelector('[data-notice]');
      this.countEl = this.querySelector('[data-count]');
      this.totalEl = this.querySelector('[data-total]');
      this.wasEl = this.querySelector('[data-was]');
      this.submitBtn = this.querySelector('[data-submit]');
      this.errorEl = this.querySelector('[data-error]');

      this.lookVariant = this.dataset.lookVariant;
      this.lookPrice = parseInt(this.dataset.lookPrice, 10) || 0;
      // Look en stock et tous ses articles aussi : seul cas où le tarif pack
      // s'applique. Calculé côté Liquid, qui connaît les stocks.
      this.lookDispo = this.dataset.lookComplete === 'true';
      this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';

      // Seuls ces articles-là peuvent être retirés ou partir seuls au panier.
      this.vendables = this.items.filter((el) => el.dataset.sellable === 'true');

      this.trigger.addEventListener('click', () => this.ouvrir());
      this.querySelector('[data-close]').addEventListener('click', () => this.fermer());
      this.overlay.addEventListener('mousedown', (evt) => {
        if (evt.target === this.overlay) this.fermer();
      });
      this.surTouche = this.surTouche.bind(this);

      this.items.forEach((item) => {
        const bouton = item.querySelector('[data-toggle]');
        if (bouton) bouton.addEventListener('click', () => this.basculer(item));
      });

      this.submitBtn.addEventListener('click', () => this.valider());

      this.rafraichir();

      // La fenêtre est rendue dans la colonne produit, dont le plan
      // d'empilement est inférieur à celui de l'en-tête collant : sur
      // ordinateur, l'en-tête la recouvrait quel que soit son z-index. Placée
      // directement dans <body>, elle passe au-dessus de tout.
      document.body.appendChild(this.overlay);
      this.dataset.pret = '';
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.surTouche);
    }

    /* --------------------------------------------------- ouverture / fermeture */

    ouvrir() {
      this.overlay.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.surTouche);
      // La page ne doit pas défiler derrière la fenêtre.
      this.scrollBloque = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.querySelector('[data-close]').focus();
    }

    fermer() {
      this.overlay.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.surTouche);
      document.body.style.overflow = this.scrollBloque || '';
      this.trigger.focus();
    }

    surTouche(evt) {
      if (evt.key === 'Escape') { this.fermer(); return; }
      if (evt.key !== 'Tab') return;

      // Le clavier reste dans la fenêtre tant qu'elle est ouverte.
      const cibles = this.dialog.querySelectorAll('button:not([disabled])');
      if (cibles.length === 0) return;
      const premier = cibles[0];
      const dernier = cibles[cibles.length - 1];
      if (evt.shiftKey && document.activeElement === premier) {
        evt.preventDefault(); dernier.focus();
      } else if (!evt.shiftKey && document.activeElement === dernier) {
        evt.preventDefault(); premier.focus();
      }
    }

    /* ------------------------------------------------------------ sélection */

    basculer(item) {
      if (item.dataset.sellable !== 'true') return;
      if (item.hasAttribute('data-kept')) item.removeAttribute('data-kept');
      else item.setAttribute('data-kept', '');
      this.rafraichir();
    }

    /** Articles qui partiraient au panier si le look est modifié. */
    gardes() {
      return this.vendables.filter((el) => el.hasAttribute('data-kept'));
    }

    /** Aucun article vendable seul n'a été retiré. */
    toutGarde() {
      return this.gardes().length === this.vendables.length;
    }

    /**
     * Le tarif pack s'applique : look en stock, et rien de retiré. Les
     * articles invendables seuls n'entrent pas dans le calcul — ils sont
     * inclus d'office dans le produit « look ».
     */
    estLookEntier() {
      return this.lookDispo && this.toutGarde();
    }

    rafraichir() {
      const entier = this.estLookEntier();

      // Les articles invendables seuls suivent l'état du look : dedans tant
      // qu'il est entier, grisés dès qu'il est modifié.
      this.items.forEach((el) => {
        if (el.dataset.sellable === 'true') return;
        el.toggleAttribute('data-kept', entier);
      });

      const gardes = this.gardes();
      const nbArticles = entier ? this.items.length : gardes.length;

      const total = entier
        ? this.lookPrice
        : gardes.reduce((s, el) => s + (parseInt(el.dataset.price, 10) || 0), 0);

      // Prix barré : seulement sur le look entier, où il dit l'économie faite.
      const sommeUnitaire = this.items.reduce(
        (s, el) => s + (parseInt(el.dataset.price, 10) || 0), 0
      );

      const pluriel = nbArticles > 1 ? 's' : '';
      let intitule = 'Votre sélection personnalisée';
      if (entier) intitule = 'Look complet';
      else if (!this.lookDispo && this.toutGarde()) intitule = 'Articles disponibles';
      this.countEl.textContent = `${intitule} - ${nbArticles} article${pluriel}`;

      this.totalEl.textContent = this.formaterPrix(total);
      this.totalEl.classList.toggle('look-editor__total-value--promo', entier);
      this.wasEl.hidden = !entier || sommeUnitaire <= this.lookPrice;
      if (!this.wasEl.hidden) this.wasEl.textContent = this.formaterPrix(sommeUnitaire);

      // Look épuisé : son propre message dit déjà que le prix est unitaire,
      // inutile d'en empiler un second.
      this.notice.hidden = entier || !this.lookDispo;
      this.submitBtn.disabled = !this.peutValider();
      this.errorEl.hidden = true;
    }

    /* -------------------------------------------------------------- panier */

    /** Le look entier (forcément en stock), ou au moins un article. */
    peutValider() {
      return this.estLookEntier() || this.gardes().length > 0;
    }

    async valider() {
      if (!this.peutValider()) return;
      const gardes = this.gardes();
      const entier = this.estLookEntier();

      this.submitBtn.disabled = true;
      this.errorEl.hidden = true;

      // Look entier → le produit « look », qui porte la remise.
      // Sinon → chaque article restant, à son prix unitaire.
      const items = entier
        ? [{ id: this.lookVariant, quantity: 1 }]
        : gardes.map((el) => ({ id: el.dataset.variant, quantity: 1 }));

      // On demande en même temps le rendu des sections du panier, comme le fait
      // le formulaire du thème : sans elles, l'en-tête et le tiroir afficheraient
      // encore l'ancien contenu.
      const sections = ['page-header', 'cart-drawer']
        .map((sel) => document.querySelector(sel))
        .filter((el) => el);

      try {
        const reponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({
            items: items,
            sections: sections.map((el) => el.dataset.sectionId).join(','),
            sections_url: window.location.pathname
          })
        });
        const data = await reponse.json();
        if (!reponse.ok) throw new Error(data.description || data.message || 'erreur');

        if (data.sections) {
          sections.forEach((el) => {
            if (data.sections[el.dataset.sectionId]) {
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
            }
          });
        }

        this.fermer();
        const tiroir = document.querySelector('.js-cart-drawer');
        if (tiroir && tiroir.open) tiroir.open(this.trigger);
        else document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      } catch (err) {
        this.errorEl.textContent = (err && err.message)
          ? err.message
          : "L'ajout au panier n'a pas abouti, merci de réessayer.";
        this.errorEl.hidden = false;
      } finally {
        this.submitBtn.disabled = !this.peutValider();
      }
    }

    /**
     * Met en forme un montant en centimes selon le format de la boutique
     * (ici « {{amount_with_comma_separator}} € »).
     */
    formaterPrix(centimes) {
      const valeur = (centimes / 100).toFixed(2);
      const [entier, decimales] = valeur.split('.');
      const espace = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);

      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, nom) => {
        switch (nom) {
          case 'amount_with_comma_separator': return `${espace(entier, '.')},${decimales}`;
          case 'amount_no_decimals': return espace(entier, ',');
          case 'amount_no_decimals_with_comma_separator': return espace(entier, '.');
          default: return `${espace(entier, ',')}.${decimales}`;
        }
      });
    }
  }

  customElements.define('look-editor', LookEditor);
})();
